import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Blocks, Loader2, AlertTriangle, Info } from 'lucide-react';
import { tenantModules } from '../../services/superuserApi';

/**
 * Módulos de um ginásio, vistos do portal de operador.
 *
 * Dois valores por módulo, e a diferença importa:
 *   `enabled`   — o que está escolhido.
 *   `effective` — o que está mesmo a funcionar depois de resolver dependências.
 * Um módulo pode estar ligado e na mesma não funcionar por lhe faltar outro de
 * que depende; esconder isso levaria o operador a garantir ao cliente algo que
 * não está a acontecer, por isso esse caso aparece assinalado.
 *
 * Desligar um módulo nunca apaga dados — só deixa de o expor.
 */

const GROUP_LABELS = {
  Gestao: 'Gestão',
  Operacao: 'Operação',
  Fitness: 'Fitness',
  Comercial: 'Comercial',
  Comunicacao: 'Comunicação',
  Portal: 'Portal do cliente',
};

function Toggle({ checked, disabled, onChange, title }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      title={title}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      style={{
        width: '36px', height: '20px', borderRadius: '9999px', flexShrink: 0,
        border: '1px solid ' + (checked ? 'var(--accent-primary)' : 'var(--border-strong)'),
        background: checked ? 'var(--accent-primary)' : 'transparent',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        padding: 0, position: 'relative', transition: 'background-color 150ms ease, border-color 150ms ease',
      }}
    >
      <span
        style={{
          position: 'absolute', top: '50%', left: checked ? '17px' : '2px',
          transform: 'translateY(-50%)', width: '14px', height: '14px', borderRadius: '9999px',
          background: checked ? '#fff' : 'var(--text-faint)', transition: 'left 150ms ease',
        }}
      />
    </button>
  );
}

export default function ModulesPanel({ tenantKey }) {
  const [groups, setGroups] = useState([]);
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    try {
      const res = await tenantModules.get(tenantKey);
      setGroups(res.data?.groups || []);
      setPresets(res.data?.presets || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Não foi possível ler os módulos desta instância.');
    } finally {
      setLoading(false);
    }
  }, [tenantKey]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const handleToggle = async (feature, next) => {
    setBusy(b => ({ ...b, [feature]: true }));
    try {
      await tenantModules.toggle(tenantKey, feature, next);
      // Recarrega em vez de assumir: ligar um módulo pode tornar outro efectivo
      // (ou deixar de o ser), e só o servidor resolve essa cadeia.
      await load();
    } catch (e) {
      const msg = e.response?.data?.message;
      toast.error(msg || 'Não foi possível alterar este módulo.');
    } finally {
      setBusy(b => ({ ...b, [feature]: false }));
    }
  };

  const handlePreset = async (name) => {
    setBusy(b => ({ ...b, [`preset:${name}`]: true }));
    try {
      await tenantModules.applyPreset(tenantKey, name);
      await load();
      toast.success(`Preset "${name}" aplicado.`);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Não foi possível aplicar o preset.');
    } finally {
      setBusy(b => ({ ...b, [`preset:${name}`]: false }));
    }
  };

  const allFeatures = groups.flatMap(g => g.features || []);
  const activeCount = allFeatures.filter(f => f.effective).length;

  if (loading) {
    return (
      <div className="su-card" style={{ padding: '1.25rem', marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.625rem', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
        <Loader2 size={16} style={{ animation: 'su-spin 1s linear infinite' }} />
        A carregar módulos...
      </div>
    );
  }

  return (
    <div className="su-card" style={{ padding: '1.25rem', marginTop: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Blocks size={16} style={{ color: 'var(--accent-primary-hover)' }} />
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: 'var(--text-primary)' }}>Módulos</h3>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-faint)' }}>
            {activeCount}/{allFeatures.length} activos
          </span>
        </div>

        {presets.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-faint)' }}>Presets:</span>
            {presets.map(p => (
              <button
                key={p}
                className="su-btn su-btn-ghost su-btn-sm"
                disabled={!!busy[`preset:${p}`]}
                onClick={() => handlePreset(p)}
              >
                {busy[`preset:${p}`] && <Loader2 size={12} style={{ animation: 'su-spin 1s linear infinite' }} />}
                {p}
              </button>
            ))}
          </div>
        )}
      </div>

      <p style={{ margin: '0 0 1rem', fontSize: '0.8125rem', color: 'var(--text-faint)', lineHeight: 1.6 }}>
        Desligar um módulo esconde-o do ginásio, mas <strong style={{ color: 'var(--text-muted)' }}>não apaga dados</strong> — voltar a ligar devolve tudo como estava.
        Os presets são apenas atalhos de preenchimento.
      </p>

      {groups.map(g => (
        <div key={g.group} style={{ marginBottom: '1.25rem' }}>
          <div style={{
            fontSize: '0.6875rem', fontWeight: '600', color: 'var(--text-faint)',
            textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.5rem', paddingLeft: '0.125rem',
          }}>
            {GROUP_LABELS[g.group] || g.group}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.125rem' }}>
            {(g.features || []).map(f => {
              const blocked = f.enabled && !f.effective;
              return (
                <div
                  key={f.feature}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.875rem',
                    padding: '0.625rem 0.75rem', borderRadius: 'var(--radius-md)',
                    background: f.effective ? 'rgba(2,6,23,0.5)' : 'transparent',
                  }}
                >
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.875rem', color: f.effective ? 'var(--text-primary)' : 'var(--text-faint)', fontWeight: '500' }}>
                        {f.name || f.feature}
                      </span>
                      {blocked && (
                        <span className="su-badge su-badge-warning">
                          <AlertTriangle size={11} />
                          Requer {(f.missingDependencies || []).join(', ')}
                        </span>
                      )}
                    </div>
                    {f.description && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-faint)', marginTop: '0.125rem' }}>
                        {f.description}
                      </div>
                    )}
                  </div>

                  {busy[f.feature]
                    ? <Loader2 size={16} style={{ animation: 'su-spin 1s linear infinite', color: 'var(--text-muted)', flexShrink: 0 }} />
                    : (
                      <Toggle
                        checked={f.enabled}
                        onChange={(next) => handleToggle(f.feature, next)}
                        title={f.enabled ? 'Desligar módulo' : 'Ligar módulo'}
                      />
                    )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {allFeatures.length === 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-faint)', fontSize: '0.875rem', padding: '0.75rem 0' }}>
          <Info size={15} />
          Esta instância ainda não reportou módulos.
        </div>
      )}
    </div>
  );
}
